import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Navbar } from "./components/navbar";
import Home from "./pages/home";
import Auth from "./pages/auth";
import CreateRecipe from "./pages/CreateRecipe";
import SavedRecipes from "./pages/savedrecipes";
import SearchRecipes from "./pages/searchrecipes";
import RecipeDetails from "./pages/recipedetails";
import OtherRecipeDetails from "./pages/otherrecipedetails";
import EditRecipe from "./pages/editrecipe";
import IngredientManagement from "./pages/IngredientManagement";
import EditIngredient from "./pages/EditIngredient";
import IngredientCategories from "./pages/IngredientCategories";
import Profile from "./pages/profile";
import Feed from "./pages/feed";
import SearchUsers from "./pages/searchusers";
import { MealBudgetPlanner } from "./components/MealBudgetPlanner";
import ShoppingList from "./pages/ShoppingList";
import Cupboard from "./pages/cupboard";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import UserPreferences from "./pages/UserPreferences";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import OfflineIndicator from "./components/OfflineIndicator";
import GoogleAuthSuccess from './pages/GoogleAuthSuccess';
import CompleteGoogleRegistration from './pages/CompleteGoogleRegistration';
import TermsAndConditions from './pages/TermsAndConditions';
import MealPlanningNew from './pages/MealPlanningNew';
import MealPlanDetails from './pages/MealPlanDetails';
import MealPlanningPreferences from './pages/MealPlanningPreferences';
import AIMealPlanning from './pages/AIMealPlanning';
import MealPrepDraft from './pages/MealPrepDraft';
import AdminPowerUsers from './pages/AdminPowerUsers';
import AdminDiscountCodes from './pages/AdminDiscountCodes';
import MarketingRecipe from './pages/MarketingRecipe';
import Premium from './pages/Premium';
import PremiumCheckout from './pages/PremiumCheckout';
import PremiumSuccess from './pages/PremiumSuccess';
import PremiumTerms from './pages/PremiumTerms';
import OAuthAuthorize from './pages/OAuthAuthorize';
import McpConnect from './pages/McpConnect';
import RegionPromptModal from './components/RegionPromptModal';
import { initMobileUtils } from './utils/mobileUtils';
import { axiosInstance } from './config';
import './App.css';
import './styles/verifyEmail.css';

function App() {
  const [showRegionPrompt, setShowRegionPrompt] = useState(false);

  useEffect(() => {
    document.body.style.visibility = 'visible';
    initMobileUtils();

    // Show the region prompt if the user is logged in but has no region set
    const checkRegion = async () => {
      const userId = localStorage.getItem('userID');
      const region = localStorage.getItem('preferredRegion');
      if (!userId) {
        setShowRegionPrompt(false);
        return;
      }

      if (region) {
        setShowRegionPrompt(false);
        return;
      }

      try {
        const response = await axiosInstance.get('/auth/validate-token');
        const tokenIsValid = response?.data?.valid === true || response?.status === 200;
        if (!tokenIsValid) {
          setShowRegionPrompt(false);
          return;
        }
      } catch (error) {
        setShowRegionPrompt(false);
        return;
      }

      try {
        const profileResponse = await axiosInstance.get(`/users/profile/${userId}`);
        const serverRegion = profileResponse?.data?.user?.preferredRegion || '';
        const serverVitality = Boolean(profileResponse?.data?.user?.vitalityFirst);

        if (serverRegion) {
          localStorage.setItem('preferredRegion', serverRegion);
          localStorage.setItem('vitalityFirst', serverVitality ? 'true' : 'false');
          window.dispatchEvent(new CustomEvent('region-updated', {
            detail: {
              preferredRegion: serverRegion,
              vitalityFirst: serverVitality
            }
          }));
          setShowRegionPrompt(false);
          return;
        }
      } catch (error) {
        // If profile fetch fails, keep existing behavior and show prompt for logged-in users with no region.
      }

      setShowRegionPrompt(true);
    };

    checkRegion();

    // Also trigger after a fresh login
    const handleAuthUpdated = () => checkRegion();
    window.addEventListener('auth-token-updated', handleAuthUpdated);
    return () => window.removeEventListener('auth-token-updated', handleAuthUpdated);
  }, []);

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <OfflineIndicator />
        <main className="pt-20 md:pt-28">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/login" element={<Auth />} />
            <Route path="/auth/register" element={<Auth />} />
            <Route path="/auth/google/success" element={<GoogleAuthSuccess />} />
            <Route path="/auth/google/complete-registration" element={<CompleteGoogleRegistration />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/createrecipe" element={<CreateRecipe />} />
            <Route path="/savedrecipes" element={<SavedRecipes />} />
            <Route path="/searchrecipes" element={<SearchRecipes />} />
            <Route path="/recipe/:recipeId" element={<RecipeDetails />} />
            <Route path="/view-recipe/:recipeId" element={<OtherRecipeDetails />} />
            <Route path="/edit-recipe/:recipeId" element={<EditRecipe />} />
            <Route path="/ingredient-management" element={<IngredientManagement />} />
            <Route path="/ingredient-categories" element={<IngredientCategories />} />
            <Route path="/edit-ingredient/:id" element={<EditIngredient />} />
            <Route path="/profile/:userId" element={<Profile />} />
            <Route path="/feed" element={<Feed />} />
            <Route path="/search-users" element={<SearchUsers />} />
            <Route path="/meal-budget" element={<MealBudgetPlanner />} />
            <Route path="/meal-planning" element={<MealPlanningNew />} />
            <Route path="/meal-planning/ai" element={<AIMealPlanning />} />
            <Route path="/meal-planning/preferences" element={<MealPlanningPreferences />} />
            <Route path="/meal-plan/:planId" element={<MealPlanDetails />} />
            <Route path="/meal-prep-draft" element={<MealPrepDraft />} />
            <Route path="/shopping-list" element={<ShoppingList />} />
            <Route path="/cupboard" element={<Cupboard />} />
            <Route path="/preferences" element={<UserPreferences />} />
            <Route path="/admin/power-users" element={<AdminPowerUsers />} />
            <Route path="/admin/discount-codes" element={<AdminDiscountCodes />} />
            <Route path="/dev/marketing" element={<MarketingRecipe />} />
            <Route path="/premium" element={<Premium />} />
            <Route path="/premium/checkout" element={<PremiumCheckout />} />
            <Route path="/premium/success" element={<PremiumSuccess />} />
            <Route path="/premium/terms" element={<PremiumTerms />} />
            <Route path="/authorize" element={<OAuthAuthorize />} />
            <Route path="/mcp/connect" element={<McpConnect />} />
          </Routes>
        </main>
        <PWAInstallPrompt />
        {showRegionPrompt && (
          <RegionPromptModal onClose={() => setShowRegionPrompt(false)} />
        )}
      </div>
    </Router>
  );
}

export default App;
